/**
 * Material Traceability cost-model (frontend mirror of the backend).
 *
 * Lets the cost-input panel show the derived per-pipe rate, implied margin, and
 * module total live as a rep edits inputs, without a round-trip to the backend.
 * The numbers match backend/app/services/pricing/utils/material_cost_model.py and
 * nre_cost_model.py; the backend remains the source of truth on Calculate.
 */

export const MINUTES_PER_HOUR = 60;
export const MARGIN_FLOOR = 0.5;

export const TIER_STORAGE_ONLY = 'storage_only';
export const TIER_EXTENDED = 'extended';

export type ServiceTier = typeof TIER_STORAGE_ONLY | typeof TIER_EXTENDED;

export interface PerPipeInputs {
  reportsPerPipe: number;
  autoExtractRate: number;
  minutesPerException: number;
  hourlyRate: number;
  computeCostPerDoc: number;
  storagePerPipe: number;
  qaOverheadPct: number;
  marginTarget: number;
}

/** Global defaults, mirroring the backend material_config locked defaults. */
export const MATERIAL_COST_DEFAULTS: PerPipeInputs = {
  reportsPerPipe: 19,
  autoExtractRate: 0.98,
  minutesPerException: 4,
  hourlyRate: 100,
  computeCostPerDoc: 0.04,
  storagePerPipe: 0.04,
  qaOverheadPct: 0.1,
  marginTarget: 0.75,
};

export interface NREInputs {
  setupHours: number;
  perUnitHours: number;
  validationOverhead: number;
  engineerRate: number;
  marginTarget: number;
}

/** Global NRE defaults, mirroring the backend nre_config locked defaults. */
export const NRE_DEFAULTS: NREInputs = {
  setupHours: 8,
  perUnitHours: 2,
  validationOverhead: 0.25,
  engineerRate: 100,
  marginTarget: 0.75,
};

export const PERCENT_PER_FRACTION = 100;

/**
 * Format-derived auto-extract rate per mill arrival format, as a fraction [0, 1].
 * Mirrors the backend autoExtractRateByFormat locked defaults. A format absent
 * from this map falls back to the global autoExtractRate.
 */
export const AUTO_EXTRACT_RATE_BY_FORMAT: Record<string, number> = {
  nativeDigital: 0.99,
  api: 0.99,
  spreadsheet: 0.98,
  scannedPdf: 0.95,
  handwritten: 0.85,
};

/** The format-derived auto-extract rate (fraction) for a mill format. */
export const formatAutoExtractRate = (format: string): number =>
  AUTO_EXTRACT_RATE_BY_FORMAT[format] ?? MATERIAL_COST_DEFAULTS.autoExtractRate;

/** Convert a fraction [0, 1] to a percent (0-100), or undefined when absent. */
export const fractionToPercent = (fraction: number | undefined): number | undefined =>
  fraction == null ? undefined : fraction * PERCENT_PER_FRACTION;

/** Convert a percent input string to a fraction [0, 1], or undefined when blank. */
export const percentToFraction = (value: string): number | undefined => {
  if (value === '') return undefined;
  const percent = Math.min(PERCENT_PER_FRACTION, Math.max(0, Number(value) || 0));
  return percent / PERCENT_PER_FRACTION;
};

/** Difficulty multiplier per mill arrival format. */
export const DIFFICULTY_MULTIPLIERS: Record<string, number> = {
  api: 0.5,
  spreadsheet: 1.0,
  scannedPdf: 2.0,
  handwritten: 3.0,
};

export interface PerPipeResult {
  extractionCompute: number;
  exceptionLabour: number;
  qaOverhead: number;
  storage: number;
  costPerPipe: number;
  computedPricePerPipe: number;
  pricePerPipe: number;
  impliedMargin: number;
  belowMarginFloor: boolean;
}

const costToPrice = (cost: number, margin: number): number => cost / (1 - margin);

/** Derive the per-pipe price from the cost build-up for the given tier. */
export const perPipePrice = (
  inputs: PerPipeInputs,
  tier: ServiceTier = TIER_EXTENDED,
  manualOverride?: number,
  marginFloor: number = MARGIN_FLOOR
): PerPipeResult => {
  let extractionCompute = 0;
  let exceptionLabour = 0;
  let qaOverhead = 0;

  if (tier !== TIER_STORAGE_ONLY) {
    extractionCompute = inputs.reportsPerPipe * inputs.computeCostPerDoc;
    const exceptionsPerPipe = inputs.reportsPerPipe * (1 - inputs.autoExtractRate);
    exceptionLabour =
      exceptionsPerPipe * (inputs.minutesPerException / MINUTES_PER_HOUR) * inputs.hourlyRate;
    qaOverhead = exceptionLabour * inputs.qaOverheadPct;
  }

  // Storage scales with the report count, the same as extraction and the backend.
  const storage = inputs.reportsPerPipe * inputs.storagePerPipe;
  const costPerPipe = extractionCompute + exceptionLabour + qaOverhead + storage;
  const computedPricePerPipe = costToPrice(costPerPipe, inputs.marginTarget);

  const pricePerPipe = manualOverride != null ? manualOverride : computedPricePerPipe;
  const impliedMargin = pricePerPipe > 0 ? 1 - costPerPipe / pricePerPipe : 0;

  return {
    extractionCompute,
    exceptionLabour,
    qaOverhead,
    storage,
    costPerPipe,
    computedPricePerPipe,
    pricePerPipe,
    impliedMargin,
    belowMarginFloor: impliedMargin < marginFloor,
  };
};

export interface NREResult {
  buildHours: number;
  totalHours: number;
  cost: number;
  computedPrice: number;
  price: number;
}

/**
 * Price one mill's onboarding NRE from its format difficulty and report-type
 * count. An unknown format is treated as zero build difficulty, so only the
 * setup hours are priced.
 */
export const nreCostPrice = (
  format: string,
  reportTypes: number,
  inputs: NREInputs = NRE_DEFAULTS,
  override?: number
): NREResult => {
  const difficulty = DIFFICULTY_MULTIPLIERS[format] ?? 0;
  const buildHours = inputs.perUnitHours * difficulty * reportTypes;
  const totalHours = (inputs.setupHours + buildHours) * (1 + inputs.validationOverhead);
  const cost = totalHours * inputs.engineerRate;
  const computedPrice = costToPrice(cost, inputs.marginTarget);
  const price = override != null ? override : computedPrice;
  return { buildHours, totalHours, cost, computedPrice, price };
};
